import React from "react";
import {
  Box,
  Chip,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  CircularProgress,
} from "@mui/material";

// Фильтры вынесены в отдельный компонент чтобы не ререндерить таблицу при открытии Select
const FilterRow = React.memo(({ filter, onChangeFilter, selectAllState, onSelectAll }) => (
  <TableRow>
    <TableCell padding="checkbox" sx={{ width: 48 }}>
      <Checkbox
        checked={selectAllState.checked}
        indeterminate={selectAllState.indeterminate}
        onChange={onSelectAll}
      />
    </TableCell>
    <TableCell>
      <TextField
        value={filter.address}
        name="address"
        placeholder="Адрес"
        onChange={onChangeFilter}
        fullWidth
        size="small"
        variant="standard"
        InputProps={{ disableUnderline: true }}
        sx={{ "& input": { fontSize: 14, py: 0.5 } }}
      />
    </TableCell>
    <TableCell>
      <TextField
        value={filter.model}
        name="model"
        placeholder="Вендор"
        onChange={onChangeFilter}
        fullWidth
        size="small"
        variant="standard"
        InputProps={{ disableUnderline: true }}
        sx={{ "& input": { fontSize: 14, py: 0.5 } }}
      />
    </TableCell>
    <TableCell>
      <TextField
        value={filter.cam_serial}
        name="cam_serial"
        placeholder="Серия"
        onChange={onChangeFilter}
        fullWidth
        size="small"
        variant="standard"
        InputProps={{ disableUnderline: true }}
        sx={{ "& input": { fontSize: 14, py: 0.5 } }}
      />
    </TableCell>
    <TableCell sx={{ minWidth: 140 }}>
      <Select
        value={filter.direction}
        name="direction"
        onChange={onChangeFilter}
        displayEmpty
        fullWidth
        size="small"
        variant="standard"
        disableUnderline
        sx={{ fontSize: 14 }}
        MenuProps={{ disableScrollLock: true, slotProps: { backdrop: { sx: { backgroundColor: "transparent" } } } }}
      >
        <MenuItem value="">Направление</MenuItem>
        <MenuItem value="Встречное">Встречное</MenuItem>
        <MenuItem value="Попутное">Попутное</MenuItem>
      </Select>
    </TableCell>
    <TableCell sx={{ minWidth: 160 }}>
      <Select
        value={filter.class}
        name="class"
        onChange={onChangeFilter}
        displayEmpty
        fullWidth
        size="small"
        variant="standard"
        disableUnderline
        sx={{ fontSize: 14 }}
        MenuProps={{ disableScrollLock: true, slotProps: { backdrop: { sx: { backgroundColor: "transparent" } } } }}
      >
        <MenuItem value="">Тип устройства</MenuItem>
        <MenuItem value="Стационарный">Стационарный</MenuItem>
        <MenuItem value="Передвижной">Передвижной</MenuItem>
        <MenuItem value="Мобильный">Мобильный</MenuItem>
      </Select>
    </TableCell>
  </TableRow>
));
FilterRow.displayName = 'FilterRow';

const TableComponent = ({ items = [], selected = [], isLoading, onChangeSelected, onChangeMultiple }) => {
  const [filter, setFilter] = React.useState({
    address: '',
    model: '',
    cam_serial: '',
    direction: '',
    class: '',
  });

  const handleChangeFilter = React.useCallback(({ target }) => {
    setFilter(prev => ({ ...prev, [target.name]: target.value }));
  }, []);

  const filteredItems = React.useMemo(() => {
    if (!items || items.length === 0) return [];
    const { address, model, cam_serial, direction, class: cls } = filter;
    if (!address && !model && !cam_serial && !direction && !cls) return items;

    return items.filter((item) => {
      if (address && !fastSearch(item?.location_cam, address)) return false;
      if (model && !fastSearch(item?.model, model)) return false;
      if (cam_serial && !fastSearch(item?.cam_serial, cam_serial)) return false;
      if (direction && String(item?.direction || '').toLowerCase() !== direction.toLowerCase()) return false;
      if (cls && String(item?.class || '').toLowerCase() !== cls.toLowerCase()) return false;
      return true;
    });
  }, [items, filter]);

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const selectAllState = React.useMemo(() => {
    if (filteredItems.length === 0) return { checked: false, indeterminate: false };
    let count = 0;
    for (const item of filteredItems) {
      if (selectedSet.has(item.complex_id)) count++;
    }
    return {
      checked: count === filteredItems.length,
      indeterminate: count > 0 && count < filteredItems.length,
    };
  }, [filteredItems, selectedSet]);

  const handleSelectAll = React.useCallback(() => {
    const ids = filteredItems.map(item => item.complex_id);
    onChangeMultiple(ids, !selectAllState.checked);
  }, [filteredItems, selectAllState.checked, onChangeMultiple]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: "20px", pb: "4px" }}>
      {selected.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={`Выбрано: ${selected.length}`}
            size="small"
            sx={{ backgroundColor: "#E0E7FF", color: "#432DD7", fontWeight: 500 }}
          />
        </Box>
      )}

      <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
        <Table size="small" stickyHeader sx={{ border: "1px solid #E4E4E7", borderRadius: "8px", overflow: "hidden" }}>
          <TableHead>
            <FilterRow
              filter={filter}
              onChangeFilter={handleChangeFilter}
              selectAllState={selectAllState}
              onSelectAll={handleSelectAll}
            />
          </TableHead>
          <TableBody>
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <TableRowItem
                  key={item.complex_id}
                  item={item}
                  isChecked={selectedSet.has(item.complex_id)}
                  onChangeSelected={onChangeSelected}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#71717A" }}>
                  Нет данных для отображения
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

const fastSearch = (text, searchTerm) => {
  if (!searchTerm) return true;
  return String(text || '').toLowerCase().includes(searchTerm.toLowerCase().trim());
};

const TableRowItem = React.memo(({ item, isChecked, onChangeSelected }) => {
  const handleChange = React.useCallback(() => {
    onChangeSelected(item.complex_id);
  }, [item.complex_id, onChangeSelected]);

  return (
    <TableRow
      hover
      onClick={handleChange}
      sx={{ cursor: "pointer", "&.MuiTableRow-hover:hover .MuiTableCell-root": { backgroundColor: "#FAFAFA" } }}
    >
      <TableCell padding="checkbox" sx={{ width: 48 }}>
        <Checkbox checked={isChecked} tabIndex={-1} />
      </TableCell>
      <TableCell>{item?.location_cam}</TableCell>
      <TableCell>{item?.model}</TableCell>
      <TableCell>{item?.cam_serial}</TableCell>
      <TableCell>{item?.direction}</TableCell>
      <TableCell>{item?.class}</TableCell>
    </TableRow>
  );
});
TableRowItem.displayName = 'TableRowItem';

export default React.memo(TableComponent);
